import {
  type DestinationSize,
  formatSizeBreakdown,
  SHIPPING_REGION_LABELS,
  type ShippingAddress,
  type ShippingRegion,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount, formatKrw } from '@/shared/lib';
import { ShippingAddressFields } from '@/shared/ui';

interface PlacementDestinationCardProps {
  /** 1부터 — 카드 제목 "배송지 N" 이 묶음(group) 이름이다. */
  readonly order: number;
  readonly quantity: number;
  readonly sizes: readonly DestinationSize[];
  /** 발행본의 이 배송지 배송비 줄 금액 — 줄이 없으면 null('-'). */
  readonly shippingFee: number | null;
  /** 견적 배송지 우편번호의 지역 — 알 수 없으면 null('-', 지역 제한 없음, 교차 조정 R1). */
  readonly region: ShippingRegion | null;
  readonly value: ShippingAddress;
  readonly errors?: Partial<Record<keyof ShippingAddress, string>>;
  readonly disabled?: boolean;
  /** 바뀐 칸만 넘긴다 — 주문하기 폼이 이전 값과 병합한다. */
  readonly onChange: (patch: Partial<ShippingAddress>) => void;
}

/**
 * 주문하기 배송지 카드(SUPER-86 spec §5-3) — 수량·배송비는 확정견적 값이라 읽기 전용으로 적고, 주소 다섯 칸만 고친다.
 * 지역 구분이 바뀌는 주소는 배송비가 달라지므로 폼이 우편번호 칸에 안내를 단다(D10).
 */
export const PlacementDestinationCard = ({
  order,
  quantity,
  sizes,
  shippingFee,
  region,
  value,
  errors,
  disabled = false,
  onChange,
}: PlacementDestinationCardProps): ReactElement => (
  <fieldset disabled={disabled} className="min-w-0 border-t border-foreground/15 pt-5">
    <legend className="float-left text-base leading-9 font-semibold">배송지 {order}</legend>
    <div className="clear-both flex flex-col gap-5 pt-4">
      <p className="flex flex-wrap gap-x-6 gap-y-1 text-sm tabular-nums">
        <span>
          수량 {formatCount(quantity)}개
          {sizes.length > 0 ? (
            <span className="ml-2 text-xs text-muted-foreground">{formatSizeBreakdown(sizes)}</span>
          ) : null}
        </span>
        <span>
          배송비 {shippingFee === null ? '-' : formatKrw(shippingFee)} ·{' '}
          {region === null ? '-' : SHIPPING_REGION_LABELS[region]}
        </span>
      </p>
      <ShippingAddressFields value={value} errors={errors} onChange={onChange} />
    </div>
  </fieldset>
);
